import React, { useRef, useMemo, useState, useEffect } from 'react'
import loadable from '@loadable/component'
import { useLocation } from 'react-router-dom'
import useSessionData from 'app/utils/useSessionData'
import { useSessionRecording } from 'lib/posthog'
import { posthog } from 'posthog-js'
import { FLAG as SCHEDULE_FLAG } from './form/scheduler'

const EXPERIMENT_FLAGS = [
  'loco_lead_form_vending',
  SCHEDULE_FLAG,
]

const EXPERIMENT_VARIANTS = {
  TEST: 'test',
  CONTROL: 'control',
}

const shownVariant = function(featureFlagValue, queryParamValue) {
  if (queryParamValue === 'true') {
    return EXPERIMENT_VARIANTS.TEST
  }

  if (queryParamValue) {
    return queryParamValue
  }

  return featureFlagValue || EXPERIMENT_VARIANTS.CONTROL
}

const LeadFormModal = loadable(() => import('./LeadFormModal'), {
  ssr: false
})

export default function LeadModal(props) {
  const ref = useRef()
  const location = useLocation()
  const [sessionData, setSessionData] = useSessionData()
  const [experiments, setExperiments] = useState({})
  const recording = useSessionRecording({ auto: false })
  const initialData = props.initialData || {}

  const getInitialData = () => ({
    foods: [],
    meals: [],
    contact: {
      ...sessionData.contact,
      sms_opt_in: false
    },
    address: {
      country_code: 'US',
    },
    ...initialData,
    meta: {
      ...initialData.meta,
      posthog_experiments: {
        ...initialData.meta?.posthog_experiments,
        ...experiments
      }
    },
    location_status: 'approximate',
    timeframe: 'asap',
  })

  const [data, setData] = useState(() => {
    return getInitialData()
  })

  // Allow the user to manually turn on the experiment using a query parameter
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return EXPERIMENT_FLAGS.reduce((acc, key) => (
      { ...acc, [key]: params.get(key) }
    ), {})
  }, [location])

  // Override the feature flags based on the query parameters
  useEffect(() => {
    if(Object.keys(queryParams).length === 0) {
      return
    }

    const overrides = Object.entries(queryParams).reduce((acc, [key, value]) => (
      value ? { ...acc, [key]: shownVariant(experiments[key], value) } : acc
    ), {})

    if(Object.keys(overrides).length > 0) {
      // Each call replaces the whole override set, so they all go in one.
      // A bare { key: variant } is silently ignored by posthog-js; only { flags } persists.
      posthog.featureFlags.overrideFeatureFlags({ flags: overrides })
      setExperiments({ ...experiments, ...overrides })
      reloadExperiments()
    }
  }, [queryParams])
  

  const reloadExperiments = () => {
    if(!posthog.featureFlags.hasLoadedFlags) {
      return
    }

    posthog.reloadFeatureFlags()

    posthog.onFeatureFlags(() => {
        // Get current feature flags from PostHog and update local state
      const currentFlags = posthog.featureFlags.getFlagsWithDetails()
      
      const updatedExperiments = {
        ...experiments,
        ...Object.entries(currentFlags).reduce((acc, [key, value]) => {
          if (EXPERIMENT_FLAGS.includes(key)) {
            // value will be the variant (e.g. 'test' or 'control')
            return { ...acc, [key]: value.variant }
          }
          return acc
        }, {})
      }
      
      setExperiments(updatedExperiments)
    })

  }
  
  if (!ref.current && props.isOpen) {
    ref.current = true
  }

  if (ref.current === undefined) {
    return null
  }

  return (
    <LeadFormModal
      {...props}
      experiments={experiments}
      reloadExperiments={reloadExperiments}
      posthog={posthog}
      recording={recording}
      initialData={initialData}
      getInitialData={getInitialData}
      data={data}
      setData={setData}
      sessionData={sessionData}
      setSessionData={setSessionData}
    />
  )
}
